/**
 * Shared utilities, hooks, and components for the inbox detail and compose pages.
 *
 * Extracted to eliminate ~80% duplication between EmailDetailPage and
 * PersonalEmailDetailPage, and ~60% duplication between ComposePage and
 * PersonalComposePage.
 */
import React, { useEffect, useState } from 'react';
import { formatInZone, parseInstant } from '../../lib/timezone';
import DOMPurify from 'dompurify';
import { Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';

// ── Sanitization ───────────────────────────────────────────────────────

/**
 * Sanitize email HTML for safe iframe rendering.
 * Strips scripts and event handlers while preserving email formatting
 * (tables, images, inline <style> tags for email template CSS).
 *
 * <style> is safe because both detail pages render in a sandboxed iframe
 * (sandbox="allow-same-origin", no allow-scripts) — CSS cannot leak into
 * the app DOM. DOMPurify sanitizes CSS-based XSS vectors (expression(),
 * javascript: URLs) inside <style> tags by default.
 */
const IFRAME_BASE_STYLES = `
    html, body { margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 14px; color: #1c1917; line-height: 1.5; }
    img { max-width: 100% !important; height: auto !important; }
    table { max-width: 100% !important; }
    pre { overflow-x: auto !important; white-space: pre-wrap !important; word-wrap: break-word !important; }
    * { box-sizing: border-box; }
`;

export function sanitizeEmailHtml(html) {
    if (!html) return '';
    const withBase = `<style>${IFRAME_BASE_STYLES}</style>${html}`;
    return DOMPurify.sanitize(withBase, {
        ADD_ATTR: ['target', 'sandbox'],
        ADD_TAGS: ['style'],
        FORBID_TAGS: ['script', 'iframe', 'object', 'embed'],
        FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],
        FORCE_BODY: true,  // Allows <style> tags (needed for HTML email templates)
    });
}

// ── Date formatting ────────────────────────────────────────────────────

/**
 * Format an email date with relative time + full tooltip.
 *
 * F24 fix: Always returns an object (or null for invalid dates) — never a
 * bare string. Callers can safely destructure `{ relative, full }` without
 * checking the return type. The unused `key` property was removed.
 *
 * @returns {{ relative: string, full: string } | null}
 */
export function formatEmailDate(dateStr, { zone } = {}) {
    if (!dateStr) return null;
    // parseInstant: offset-less strings are UTC instants (#532); comm_date
    // carries "+00:00" so both shapes resolve to the same instant.
    const date = parseInstant(dateStr);
    if (!date) return null;
    const now = Date.now();
    const diffMs = now - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMs / 3600000);
    const diffDay = Math.floor(diffMs / 86400000);

    let relative;
    if (diffMin < 1) relative = 'just now';
    else if (diffMin < 60) relative = `${diffMin}m ago`;
    else if (diffHr < 24) relative = `${diffHr}h ago`;
    else if (diffDay < 7) relative = `${diffDay}d ago`;
    else relative = formatInZone(date, { zone, formatStr: 'MMM d' });

    // #532: the full stamp names its zone ("Sep 14, 2026 02:12 PM (GMT+8)") in
    // the viewer's display zone — callers pass resolveDisplayZone/useDisplayZone;
    // without a zone the formatter uses the browser clock, never silent UTC.
    const full = formatInZone(date, { zone, formatStr: 'MMM d, yyyy hh:mm a (zzz)' });

    // The READER's stamp, which is not the list's.
    //
    // A list is scanned, so "3d ago" is the useful form — the reader is read,
    // and inside a thread the only question a date answers is "how far apart
    // were these two messages", which a relative stamp cannot answer without
    // the viewer doing arithmetic across every row. So the design prints the
    // wall clock, and drops the date for anything sent today because the rest
    // of the thread already establishes the day.
    const sameDay = diffDay < 1 && date.getDate() === new Date(now).getDate();
    const stamp = formatInZone(date, {
        zone,
        formatStr: sameDay ? 'h:mm a' : 'MMM d, h:mm a',
    });
    return { relative, full, stamp };
}

// ── Hooks ──────────────────────────────────────────────────────────────

/**
 * Auto-resize an iframe to fit its content height.
 * Attaches a load listener and polls briefly for dynamic content.
 *
 * @param {React.RefObject} iframeRef - ref to the iframe element
 * @param {Array} deps - extra dependencies that should trigger re-resize
 */
export function useIframeAutoResize(iframeRef, deps = []) {
    useEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe) return;
        const resize = () => {
            try {
                const h = iframe.contentWindow?.document?.body?.scrollHeight;
                if (h) iframe.style.height = (h + 20) + 'px';
            } catch (e) { /* cross-origin, keep default height */ }
        };
        iframe.addEventListener('load', resize);
        // Poll for dynamic content (images loading, etc.)
        const poll = setInterval(resize, 1000);
        const stopPoll = setTimeout(() => clearInterval(poll), 5000);
        return () => {
            iframe.removeEventListener('load', resize);
            clearInterval(poll);
            clearTimeout(stopPoll);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
}

// ── Reply and forward body builders ────────────────────────────────────

function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function quoteMetadata({ fromAddress, fromName, date, zone }) {
    const fromLine = fromName
        ? `${escapeHtml(fromName)} &lt;${escapeHtml(fromAddress || '')}&gt;`
        : escapeHtml(fromAddress || '');
    // #532: the quoted "Date:" line is labelled in the sender's display zone.
    const dateLine = escapeHtml(formatInZone(date, { zone, formatStr: 'MMM d, yyyy hh:mm a (zzz)' }));
    return { fromLine, dateLine };
}

function quoteBodyContent(bodyHtml, body) {
    if (bodyHtml) return sanitizeEmailHtml(bodyHtml);
    return escapeHtml(body || '').replace(/\r?\n/g, '<br>');
}

/** The message fields a forward is built from. At least one must be present. */
const FORWARD_MESSAGE_FIELDS = ['fromAddress', 'fromName', 'date', 'subject', 'bodyHtml', 'body'];

/** Properties only a DOM / React synthetic event carries. */
const EVENT_MARKERS = ['nativeEvent', 'currentTarget', 'preventDefault', 'stopPropagation', 'isTrusted'];

/**
 * Build a blockquote forward header for an email.
 *
 * F21 fix: Extracted from EmailDetailPage and PersonalEmailDetailPage to
 * eliminate duplication. Both pages now call this shared helper.
 *
 * ## The message argument is REQUIRED, and the failure is loud (#596)
 *
 * This threw nothing before, and a forward built from a missing message is not
 * a visible failure — it is a well-formed, EMPTY blockquote that goes out to a
 * real recipient over the user's own name. #596 shipped exactly that: a handler
 * was wired as a bare reference (`onClick={handleForward}`), so React handed it
 * a click EVENT where a message belonged. An event has none of the fields below,
 * a default parameter never applies to an argument that was passed, and every
 * `|| ''` fallback in here then did its job perfectly — producing "From: ",
 * "Subject: " and no body.
 *
 * So the three ways to arrive here without a message all throw:
 *
 *   - no argument at all,
 *   - an argument that is not a plain object (an event, a string, an array),
 *   - an object carrying none of `FORWARD_MESSAGE_FIELDS` — which is the exact
 *     shape the #596 call site produced once the event had been spread through
 *     `{ fromAddress: entry.from_address, ... }` into all-undefined values.
 *
 * A caller that legitimately has no message must not call this: decide, and
 * show the user why, rather than sending an empty quote.
 */
export function buildForwardHeader(message) {
    if (message === undefined || message === null) {
        throw new TypeError(
            'buildForwardHeader: the message argument is required. Forwarding without a '
            + 'message sends an empty quoted block to a real recipient (#596).',
        );
    }
    if (typeof message !== 'object' || Array.isArray(message)) {
        throw new TypeError(
            `buildForwardHeader: expected a message object, received ${Array.isArray(message) ? 'an array' : typeof message}.`,
        );
    }
    const eventMarker = EVENT_MARKERS.find(k => k in message);
    if (eventMarker) {
        throw new TypeError(
            `buildForwardHeader: received a DOM/synthetic event (has \`${eventMarker}\`) where a message `
            + 'was expected. A forward handler passed as a bare reference — onClick={handleForward} — is '
            + 'called with the click event; wrap it: onClick={() => handleForward(message)} (#596).',
        );
    }
    if (!FORWARD_MESSAGE_FIELDS.some(k => message[k] != null && message[k] !== '')) {
        throw new TypeError(
            `buildForwardHeader: the message carries none of ${FORWARD_MESSAGE_FIELDS.join(', ')}, `
            + 'so the forward would be an empty quoted block (#596).',
        );
    }

    const { fromAddress, fromName, date, subject, bodyHtml, body, zone } = message;
    const { fromLine, dateLine } = quoteMetadata({ fromAddress, fromName, date, zone });
    const bodyContent = quoteBodyContent(bodyHtml, body);
    return `<br><br><blockquote style="border-left:2px solid #ccc;padding-left:10px;margin-left:0;">From: ${fromLine}<br>Date: ${dateLine}<br>Subject: ${escapeHtml(subject || '')}<br><br>${bodyContent}</blockquote>`;
}


// ── Shared components ───────────────────────────────────────────────────


/**
 * Sender avatar — circular initial badge, consistent across detail pages.
 * Uses stone-100 background for all variants.
 */

export function SenderAvatar({ name, address, size = 'md' }) {
    const text = name || address || '?';
    const initial = text.charAt(0).toUpperCase() || '?';
    const sizes = {
        sm: 'w-8 h-8 text-xs',
        md: 'w-9 h-9 text-sm',
        lg: 'w-10 h-10 text-base',
    };
    return (
        <div
            className={`${sizes[size]} rounded-full bg-stone-100 flex items-center justify-center font-semibold text-stone-900 flex-shrink-0`}
            aria-hidden="true"
        >
            {initial}
        </div>
    );
}

/**
 * The CC/BCC visibility toggle, split out of CcBccFields (#509 / D1).
 *
 * The toggle lives in the To header row; the fields render as a sibling block
 * below the To typeahead. Keeping them in one component forced every caller
 * into the same markup shape and capped the editor width, so the two concerns
 * are now separate exports in this module.
 *
 * `controlsId` must match the `id` passed to CcBccFields so aria-controls
 * points at a real element.
 */
export function CcBccToggle({ show, onToggle, controlsId = 'cc-bcc-fields' }) {
    return (
        <button
            type="button"
            onClick={onToggle}
            className="text-[10px] text-stone-400 hover:text-stone-600"
            aria-expanded={show}
            aria-controls={controlsId}
            data-testid="cc-bcc-toggle"
        >
            {show ? 'Hide CC/BCC' : 'CC / BCC'}
        </button>
    );
}

/**
 * The CC/BCC input fields, without the toggle.
 *
 * Renders nothing while collapsed. When open it is ONE grid container (so the
 * two columns align) with the given `id` — the element `CcBccToggle`'s
 * aria-controls names. `onToggle` is still accepted and ignored so existing
 * callers that spread props through don't break.
 *
 * The `${placeholder} CC...` / `${placeholder} BCC...` placeholder strings are
 * load-bearing: EmailDetailPage.test.jsx's typeahead mock keys on them to tell
 * the CC and BCC inputs apart.
 */
export function CcBccFields({ show, cc, onCcChange, bcc, onBccChange, TypeaheadComponent, placeholder = 'Add...', id = 'cc-bcc-fields', onToggle }) {
    if (!show) return null;
    return (
        <div id={id} data-testid={id} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
                <Label className="text-xs">CC</Label>
                <TypeaheadComponent value={cc} onChange={onCcChange} placeholder={`${placeholder} CC...`} />
            </div>
            <div>
                <Label className="text-xs">BCC</Label>
                <TypeaheadComponent value={bcc} onChange={onBccChange} placeholder={`${placeholder} BCC...`} />
            </div>
        </div>
    );
}


/**
 * Loading skeleton for email list rows — prevents layout shift.
 */
export function InboxSkeleton({ rows = 5 }) {
    return (
        <div className="divide-y divide-stone-100" data-testid="inbox-skeleton" aria-busy="true" aria-live="polite">
            {Array.from({ length: rows }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3 animate-pulse">
                    <div className="w-56 h-4 bg-stone-100 rounded" />
                    <div className="w-56 h-4 bg-stone-100 rounded hidden md:block" />
                    <div className="flex-1 h-4 bg-stone-100 rounded" />
                    <div className="w-24 h-4 bg-stone-100 rounded" />
                    <div className="w-28 h-4 bg-stone-100 rounded" />
                </div>
            ))}
        </div>
    );
}

/**
 * Consistent loading spinner used across all inbox surfaces.
 */
export function InboxSpinner({ size = 'md', label }) {
    const sizes = { sm: 'w-4 h-4', md: 'w-5 h-5', lg: 'w-6 h-6' };
    return (
        <div className="flex items-center justify-center gap-2 py-12 text-stone-400" role="status" aria-live="polite">
            <Loader2 className={`${sizes[size]} animate-spin`} />
            {label && <span className="text-sm">{label}</span>}
        </div>
    );
}

/**
 * Consistent error state with retry button.
 */
export function InboxError({ message, detail, onRetry, retryLabel = 'Retry' }) {
    return (
        <div className="flex flex-col items-center justify-center py-12 gap-3" role="alert" aria-live="assertive">
            <p className="text-sm text-stone-500">{message || 'Something went wrong.'}</p>
            {detail && <p className="-mt-2 text-xs text-stone-400">{detail}</p>}
            {onRetry && (
                <Button variant="outline" size="sm" onClick={onRetry}>
                    {retryLabel}
                </Button>
            )}
        </div>
    );
}

/**
 * Badge for synthetic (optimistic) messages — shows 'Sending...' status.
 * Used by ThreadMessage when entry.__synthetic is true.
 */
export function SyntheticMessageBadge() {
    return (
        <span
            className="inline-flex items-center gap-1 text-[10px] font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded animate-pulse"
            data-testid="synthetic-message-badge"
        >
            <Loader2 className="w-3 h-3 animate-spin" />
            Sending...
        </span>
    );
}


